/** WorldCup-aligned self-built football market types (四大金刚 + 波胆). */
export const SPORT_MARKET_TYPES = Object.freeze({
  MATCH_WINNER: 'match_winner',
  DRAW: 'draw',
  HANDICAP: 'handicap',
  OVER_UNDER: 'over_under',
  CORRECT_SCORE: 'correct_score',
});

/** Mandatory four majors — list/detail retail requires these; CS is optional after. */
export const SPORT_MAIN_MARKET_TYPES = Object.freeze([
  SPORT_MARKET_TYPES.MATCH_WINNER,
  SPORT_MARKET_TYPES.DRAW,
  SPORT_MARKET_TYPES.HANDICAP,
  SPORT_MARKET_TYPES.OVER_UNDER,
]);

export const SPORT_MARKET_TYPE_ORDER = Object.freeze([
  ...SPORT_MAIN_MARKET_TYPES,
  SPORT_MARKET_TYPES.CORRECT_SCORE,
]);

export function sportMarketTypeOf(market) {
  return String(market?.marketType ?? market?.market_type ?? '')
    .trim()
    .toLowerCase();
}

export function isSportFiveTypeMarket(market) {
  const mt = sportMarketTypeOf(market);
  return SPORT_MARKET_TYPE_ORDER.includes(mt);
}

export function collectionHasSportFiveTypes(row) {
  const markets = Array.isArray(row?.markets) ? row.markets : [];
  return markets.some((m) => isSportFiveTypeMarket(m));
}

/** WorldCup retail gate: 独赢+平局+让球+大小 must all exist (CS-only → false). */
export function collectionHasSportMainFour(row) {
  const markets = Array.isArray(row?.markets) ? row.markets : [];
  const have = new Set(markets.map((m) => sportMarketTypeOf(m)).filter(Boolean));
  return SPORT_MAIN_MARKET_TYPES.every((mt) => have.has(mt));
}

/**
 * List card markets for SelfBuilt football:
 * keep 四大金刚 (winner/draw/handicap/OU); drop correct_score so cards stay dense.
 * PM-style list board reads these; generic MarketCard must not render five-type rows.
 */
export function filterSportFeedFeaturedMarkets(markets) {
  const list = Array.isArray(markets) ? markets : [];
  if (!list.some((m) => isSportFiveTypeMarket(m))) return list;
  const featured = list.filter((m) => {
    const mt = sportMarketTypeOf(m);
    return SPORT_MAIN_MARKET_TYPES.includes(mt);
  });
  return featured.length
    ? featured
    : list.filter((m) => sportMarketTypeOf(m) === SPORT_MARKET_TYPES.MATCH_WINNER);
}

export function sortSportMarketsByType(markets) {
  const list = Array.isArray(markets) ? [...markets] : [];
  const rank = (m) => {
    const idx = SPORT_MARKET_TYPE_ORDER.indexOf(sportMarketTypeOf(m));
    return idx >= 0 ? idx : 99;
  };
  return list.sort((a, b) => {
    const d = rank(a) - rank(b);
    if (d !== 0) return d;
    const la = String(a?.groupItemTitle ?? a?.group_item_title ?? '');
    const lb = String(b?.groupItemTitle ?? b?.group_item_title ?? '');
    return la.localeCompare(lb, undefined, { numeric: true });
  });
}

export function filterMarketsBySportType(markets, marketType) {
  const want = String(marketType ?? '')
    .trim()
    .toLowerCase();
  if (!want || want === 'all') return sortSportMarketsByType(markets);
  return sortSportMarketsByType(markets).filter((m) => sportMarketTypeOf(m) === want);
}

/** 体育详情默认下单行：优先独赢盘，否则取排序后首行 */
export function pickDefaultSportDetailBinaryRow(rows) {
  const list = Array.isArray(rows) ? rows : [];
  if (!list.length) return null;
  const sorted = sortSportMarketsByType(list);
  return (
    sorted.find((r) => sportMarketTypeOf(r) === SPORT_MARKET_TYPES.MATCH_WINNER) || sorted[0] || null
  );
}

/** SportMatchBoard 分区 id，供 depth lazy 加载对齐。 */
export function sportBinarySectionOf(row) {
  const mt = sportMarketTypeOf(row);
  if (mt === SPORT_MARKET_TYPES.MATCH_WINNER || mt === SPORT_MARKET_TYPES.DRAW) return 'winner';
  if (mt === SPORT_MARKET_TYPES.HANDICAP) return 'handicap';
  if (mt === SPORT_MARKET_TYPES.OVER_UNDER) return 'overUnder';
  if (mt === SPORT_MARKET_TYPES.CORRECT_SCORE) return 'correctScore';
  return '';
}
